import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notesV1Api, type NoteDto, type PostNotePayload } from "../api/notes";

/**
 * Notes Module v1.0 §10 — TanStack Query hooks. Cache invalidation is
 * keyed by `(ownerType, ownerId)` so a posted note refreshes both the
 * feed and the entity-tab badge in a single round-trip.
 */
export const noteKeys = {
  all: ["notes"] as const,
  byOwner: (ownerType: string, ownerId: string) =>
    [...noteKeys.all, "by-owner", ownerType, ownerId] as const,
  recentCount: (ownerType: string, ownerId: string) =>
    [...noteKeys.all, "recent-count", ownerType, ownerId] as const,
};

export function useNotesByOwner(
  ownerType: string,
  ownerId: string,
  options: { skip?: number; take?: number; enabled?: boolean } = {},
) {
  const { skip = 0, take = 50, enabled = true } = options;
  return useQuery<NoteDto[]>({
    queryKey: [...noteKeys.byOwner(ownerType, ownerId), skip, take],
    queryFn: () => notesV1Api.listByOwner(ownerType, ownerId, skip, take),
    enabled: enabled && Boolean(ownerType) && Boolean(ownerId),
  });
}

export function useNotesRecentCount(
  ownerType: string,
  ownerId: string,
  windowDays = 7,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;
  return useQuery<number>({
    queryKey: [...noteKeys.recentCount(ownerType, ownerId), windowDays],
    queryFn: () => notesV1Api.recentCount(ownerType, ownerId, windowDays),
    enabled: enabled && Boolean(ownerType) && Boolean(ownerId),
  });
}

function invalidateOwner(qc: ReturnType<typeof useQueryClient>, ownerType: string, ownerId: string) {
  void qc.invalidateQueries({ queryKey: noteKeys.byOwner(ownerType, ownerId) });
  void qc.invalidateQueries({ queryKey: noteKeys.recentCount(ownerType, ownerId) });
}

export function usePostNote(ownerType: string, ownerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PostNotePayload) => notesV1Api.post(payload),
    onSuccess: () => invalidateOwner(qc, ownerType, ownerId),
  });
}

export function useEditNote(ownerType: string, ownerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      notesV1Api.edit(id, body),
    onSuccess: () => invalidateOwner(qc, ownerType, ownerId),
  });
}

export function useDeleteNote(ownerType: string, ownerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notesV1Api.remove(id),
    onSuccess: () => invalidateOwner(qc, ownerType, ownerId),
  });
}

export function useModerateDeleteNote(ownerType: string, ownerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      notesV1Api.moderateRemove(id, reason),
    onSuccess: () => invalidateOwner(qc, ownerType, ownerId),
  });
}
